import { useState, useMemo } from "react";
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Hotel } from "lucide-react";
import { Button } from "@/components/ui/button";
import { STATUS_META } from "@/lib/constants";

const MONTH_NAMES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
];

const WEEKDAYS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

export const CalendarView = ({ clients = [], onSelectClient, labels = {} }) => {
  const [currentDate, setCurrentDate] = useState(() => new Date());

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  const goToday = () => setCurrentDate(new Date());

  // Map trips by YYYY-MM-DD
  const tripsByDate = useMemo(() => {
    const map = {};
    clients.forEach((c) => {
      const res = c.reservation || {};
      if (res.travel_date_open || !res.travel_date) return;
      const d = res.travel_date.slice(0, 10);
      if (!map[d]) map[d] = [];
      map[d].push(c);
    });
    return map;
  }, [clients]);

  // Calendar grid math
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayIndex = (new Date(year, month, 1).getDay() + 6) % 7; // Monday = 0

  const calendarDays = useMemo(() => {
    const cells = [];
    const prevMonthDays = new Date(year, month, 0).getDate();

    // Previous month padding
    for (let i = firstDayIndex - 1; i >= 0; i--) {
      cells.push({
        dayNumber: prevMonthDays - i,
        isCurrentMonth: false,
        dateKey: "",
      });
    }

    // Current month days
    for (let d = 1; d <= daysInMonth; d++) {
      const mm = String(month + 1).padStart(2, "0");
      const dd = String(d).padStart(2, "0");
      const dateKey = `${year}-${mm}-${dd}`;
      cells.push({
        dayNumber: d,
        isCurrentMonth: true,
        dateKey,
      });
    }

    // Next month padding to fill complete rows
    const totalCells = Math.ceil(cells.length / 7) * 7;
    let nextDay = 1;
    while (cells.length < totalCells) {
      cells.push({
        dayNumber: nextDay++,
        isCurrentMonth: false,
        dateKey: "",
      });
    }

    return cells;
  }, [year, month, daysInMonth, firstDayIndex]);

  const todayStr = useMemo(() => {
    const t = new Date();
    const mm = String(t.getMonth() + 1).padStart(2, "0");
    const dd = String(t.getDate()).padStart(2, "0");
    return `${t.getFullYear()}-${mm}-${dd}`;
  }, []);

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
      {/* Header */}
      <div className="p-4 sm:p-5 border-b border-slate-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 bg-slate-50/50">
        <div className="flex items-center gap-2">
          <CalendarIcon className="h-5 w-5 text-slate-500" />
          <h2 className="font-heading text-lg font-bold text-slate-800">
            {MONTH_NAMES[month]} {year}
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={goToday} className="text-xs h-8">
            Hoy
          </Button>
          <div className="flex items-center rounded-lg border border-slate-200 bg-white">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-slate-600 hover:text-slate-900"
              onClick={prevMonth}
              title="Mes anterior"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="h-4 w-px bg-slate-200" />
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-slate-600 hover:text-slate-900"
              onClick={nextMonth}
              title="Mes siguiente"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Weekdays */}
      <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-100/60 text-center text-xs font-semibold text-slate-600 py-2.5">
        {WEEKDAYS.map((w) => (
          <div key={w}>{w}</div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 divide-x divide-y divide-slate-100">
        {calendarDays.map((cell, idx) => {
          const isToday = cell.dateKey === todayStr;
          const dayTrips = cell.dateKey ? tripsByDate[cell.dateKey] || [] : [];

          return (
            <div
              key={idx}
              className={`min-h-[105px] sm:min-h-[120px] p-1.5 sm:p-2 flex flex-col transition-colors ${
                cell.isCurrentMonth ? "bg-white" : "bg-slate-50/60 text-slate-400"
              } ${isToday ? "ring-2 ring-inset ring-slate-800/40" : ""}`}
            >
              <div className="flex items-center justify-between mb-1">
                <span
                  className={`text-xs font-medium rounded-full h-5 w-5 flex items-center justify-center ${
                    isToday
                      ? "bg-slate-900 text-white font-bold"
                      : cell.isCurrentMonth
                      ? "text-slate-700"
                      : "text-slate-400"
                  }`}
                >
                  {cell.dayNumber}
                </span>
                {dayTrips.length > 0 && (
                  <span className="text-[10px] font-mono text-slate-500 bg-slate-100 px-1.5 py-0.2 rounded">
                    {dayTrips.length} {dayTrips.length === 1 ? "salida" : "salidas"}
                  </span>
                )}
              </div>

              {/* Trips in day */}
              <div className="flex-1 space-y-1 overflow-y-auto max-h-[85px]">
                {dayTrips.map((client) => {
                  const m = STATUS_META[client.status] || STATUS_META.gris;
                  return (
                    <button
                      key={client.id}
                      onClick={() => onSelectClient?.(client)}
                      title={`${client.full_name} — ${client.hotel || "Sin hotel"}`}
                      className="w-full text-left p-1 rounded border text-[11px] leading-tight flex items-center gap-1.5 transition-all hover:shadow-xs group"
                      style={{
                        backgroundColor: m.bg,
                        borderColor: m.border,
                        color: m.text,
                      }}
                    >
                      <span
                        className="h-1.5 w-1.5 rounded-full shrink-0"
                        style={{ backgroundColor: m.hex }}
                      />
                      <span className="font-semibold truncate flex-1">
                        {client.full_name}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
